import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type CreateEntityRequest,
  type EntityListItem,
} from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/utils";

// EntityPicker — v2.8.0 Day 6 — modal dialog for the "move channel to
// entity" UX. Opens from:
//   - /entities/:id 详情页 (per-channel 移动按钮)
//   - /channels/:id 详情页 (归属 entity 块的 "移动到 entity..." 按钮)
//
// Two flows wrapped in one component:
//   - **pick existing**: debounced server-side search → radio list → confirm
//   - **create new + auto-pick**: toggle to inline form → POST /api/entities
//     → result becomes the picked entity → confirm
//
// Optional reason field lands on entity_merge_history.reason and the audit
// log Details for later "why did we merge these" forensics.
//
// The component does NOT execute the move itself; it calls onPick(id, reason)
// and lets the caller invoke endpoints.moveChannelToEntity with the right
// channel id + invalidate the right queries on success. Keeps this picker
// reusable for the (future) campaign-level entity attribution flow.

interface EntityPickerProps {
  open: boolean;
  channelLabel: string;            // e.g. "@Sheldino_D" — shown in title
  excludeEntityID?: number;        // hide this row from results (currently-attached entity)
  onClose: () => void;
  onPick: (entityID: number, reason: string) => Promise<void>;
}

export function EntityPicker(props: EntityPickerProps) {
  return props.open ? <PickerImpl {...props} /> : null;
}

function PickerImpl({
  channelLabel,
  excludeEntityID,
  onClose,
  onPick,
}: EntityPickerProps) {
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<EntityListItem | null>(null);
  const [reason, setReason] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchDraft.trim()), 250);
    return () => clearTimeout(t);
  }, [searchDraft]);

  // Close on Escape — standard modal affordance.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const listQ = useQuery({
    queryKey: ["entity-picker", search],
    queryFn: () => endpoints.listEntities({ search: search || undefined, limit: 25 }),
    refetchOnWindowFocus: false,
  });

  const rows = (listQ.data?.items ?? []).filter(
    (e) => excludeEntityID === undefined || e.id !== excludeEntityID,
  );

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onPick(selected.id, reason.trim());
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        // Click on backdrop closes; clicks inside the dialog do not bubble.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-border bg-background shadow-xl">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold">
            移动 <span className="font-mono">{channelLabel}</span> 到哪个 entity?
          </h2>
        </div>

        <div className="space-y-3 px-5 py-4">
          {!createMode && (
            <>
              <Input
                autoFocus
                placeholder="搜索名称或 handle…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
              <div className="max-h-72 overflow-auto rounded border border-border">
                {listQ.isLoading && (
                  <p className="p-3 text-xs text-muted-foreground">加载中…</p>
                )}
                {listQ.isError && (
                  <p className="p-3 text-xs text-destructive">
                    加载失败:{(listQ.error as Error).message}
                  </p>
                )}
                {!listQ.isLoading && rows.length === 0 && (
                  <p className="p-3 text-xs text-muted-foreground">
                    没有匹配的 entity{excludeEntityID ? "(已排除当前所属)" : ""}
                  </p>
                )}
                {rows.length > 0 && (
                  <ul>
                    {rows.map((e) => (
                      <li key={e.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(e)}
                          className={cn(
                            "block w-full px-3 py-2 text-left text-sm hover:bg-muted",
                            selected?.id === e.id && "bg-muted",
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={selected?.id === e.id}
                              readOnly
                              tabIndex={-1}
                            />
                            <span className="font-medium">
                              {e.canonical_name}
                            </span>
                            {e.is_internal && (
                              <Badge className="bg-amber-100 text-amber-700">
                                ★ 我们
                              </Badge>
                            )}
                            {e.primary_handle && (
                              <span className="font-mono text-xs text-muted-foreground">
                                {e.primary_handle}
                              </span>
                            )}
                            <span className="ml-auto text-xs text-muted-foreground">
                              {e.channels_count} channel{e.channels_count === 1 ? "" : "s"}
                            </span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setCreateMode(true);
                  setSelected(null);
                }}
                className="text-xs text-primary hover:underline"
              >
                + 新建一个 entity
              </button>
            </>
          )}

          {createMode && (
            <CreateEntityInline
              defaultName={channelLabel.replace(/^@/, "")}
              onCancel={() => setCreateMode(false)}
              onCreated={(entity) => {
                // Convert to ListItem shape so the selection state can reuse it.
                setSelected({
                  id: entity.id,
                  canonical_name: entity.canonical_name,
                  primary_handle: entity.primary_handle,
                  is_internal: entity.is_internal,
                  channels_count: 0,
                  notes: entity.notes,
                  created_at: entity.created_at,
                });
                setCreateMode(false);
              }}
            />
          )}

          <div>
            <label className="block text-xs text-muted-foreground" htmlFor="merge-reason">
              原因(可选,会写入审计日志)
            </label>
            <Input
              id="merge-reason"
              placeholder="例如:同一人、合并 X + YT 账号…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {submitError && (
            <p className="text-xs text-destructive">{submitError}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!selected || submitting}
          >
            {submitting ? "处理中…" : "确认移动"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// CreateEntityInline — small form that POSTs /api/entities and reports the
// new row to the parent. Used by:
//   - EntityPicker's "+ 新建" sub-form (no defaults)
//   - Entities list page "+ 新建实体" modal (no defaults)
//   - EntitySelf NotSeeded bootstrap (defaultIsInternal + lockIsInternal so
//     the seed flow can't accidentally produce a KOL-shaped Bitbase entity)
// hideCancel hides the Cancel button when the form IS the whole page and
// there's nothing to close (NotSeeded use case).
export function CreateEntityInline({
  defaultName,
  defaultIsInternal,
  lockIsInternal,
  hideCancel,
  onCancel,
  onCreated,
}: {
  defaultName: string;
  defaultIsInternal?: boolean;
  lockIsInternal?: boolean;
  hideCancel?: boolean;
  onCancel: () => void;
  onCreated: (entity: import("@/lib/api").EntityDetail) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(defaultName);
  const [handle, setHandle] = useState("");
  const [isInternal, setIsInternal] = useState(defaultIsInternal ?? false);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (body: CreateEntityRequest) => endpoints.createEntity(body),
    onSuccess: (entity) => {
      // Invalidate any list cache so the new row appears immediately when
      // the operator next navigates to /entities.
      qc.invalidateQueries({ queryKey: ["entities", "list"] });
      onCreated(entity);
    },
    onError: (e) => setErr((e as Error).message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const body: CreateEntityRequest = { canonical_name: name.trim() };
    if (handle.trim()) body.primary_handle = handle.trim();
    if (isInternal) body.is_internal = true;
    if (notes.trim()) body.notes = notes.trim();
    if (!body.canonical_name) {
      setErr("canonical_name 不能为空");
      return;
    }
    createMut.mutate(body);
  };

  return (
    <form onSubmit={submit} className="space-y-2 rounded border border-border bg-muted/30 p-3">
      <p className="text-xs text-muted-foreground">新建一个 entity</p>
      <div>
        <label className="block text-xs text-muted-foreground">名称 *</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
      <div>
        <label className="block text-xs text-muted-foreground">primary handle(可选)</label>
        <Input
          placeholder="@something"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={isInternal}
          onChange={(e) => setIsInternal(e.target.checked)}
          disabled={lockIsInternal}
        />
        <span className={lockIsInternal ? "font-medium text-foreground" : ""}>
          {lockIsInternal
            ? "✓ 这是 Bitbase 自身实体(is_internal 已锁定,用于自监控)"
            : "是我们自己的账号(is_internal,用于 Bitbase 自监控)"}
        </span>
      </label>
      <div>
        <label className="block text-xs text-muted-foreground">备注(可选)</label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </div>
      {err && <p className="text-xs text-destructive">{err}</p>}
      <div className="flex justify-end gap-2 pt-1">
        {!hideCancel && (
          <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
            取消
          </Button>
        )}
        <Button size="sm" type="submit" disabled={createMut.isPending}>
          {createMut.isPending ? "创建中…" : "创建并选择"}
        </Button>
      </div>
    </form>
  );
}
